import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Plus, Pencil, Trash2, X, Factory, Package } from "lucide-react";
import { toast } from "sonner";
import MultiSelect from "@/components/MultiSelect";

const EMPTY_MACHINE = { name: "", model: "", capacity: "", notes: "" };

export default function ProductionUnits() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);   // null | new | doc
  const [products, setProducts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const res = await api.get("/production/units", { params });
      setUnits(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    // preload products for the mapping picker (single call, cached in state)
    api.get("/products", { params: { limit: 500 } }).then((r) => {
      setProducts(r.data || []);
    });
  }, []);

  const remove = async (u) => {
    if (!window.confirm(`Deactivate production unit "${u.name}"?`)) return;
    try {
      await api.delete(`/production/units/${u.id}`);
      toast.success("Production unit deactivated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to deactivate");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            placeholder="Search production units…"
            className="pl-8 h-9 text-sm bg-white"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="pu-search"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">{units.length} unit{units.length !== 1 ? "s" : ""}</span>
          <Button
            className="h-9 bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5"
            onClick={() => setEditing({ __new: true, name: "", address: "", email: "", incharge_name: "", mobile: "", machines: [], product_ids: [] })}
            data-testid="pu-add-btn"
          >
            <Plus className="h-4 w-4" />
            Add Production Unit
          </Button>
        </div>
      </div>

      <Surface padded={false}>
        <div className="overflow-auto max-h-[calc(100vh-200px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Incharge</th>
                <th className="px-4 py-2.5">Mobile</th>
                <th className="px-4 py-2.5">Email</th>
                <th className="px-4 py-2.5">Machines</th>
                <th className="px-4 py-2.5">Products</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading…</td></tr>
              )}
              {!loading && units.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">
                  <Factory className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No production units yet — click <span className="font-semibold text-gray-700">Add Production Unit</span> to create one
                </td></tr>
              )}
              {units.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50" data-testid={`pu-row-${u.id}`}>
                  <td className="px-4 py-2 font-medium text-gray-900">{u.name}</td>
                  <td className="px-4 py-2 text-gray-600">{u.incharge_name || "—"}</td>
                  <td className="px-4 py-2 text-gray-600">{u.mobile || "—"}</td>
                  <td className="px-4 py-2 text-gray-600">{u.email || "—"}</td>
                  <td className="px-4 py-2 text-gray-600">{u.machines?.length || 0}</td>
                  <td className="px-4 py-2 text-gray-600">{u.product_ids?.length || 0}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setEditing({ ...u })}
                      className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-gray-100 text-xs text-gray-700"
                      data-testid={`pu-edit-${u.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button
                      onClick={() => remove(u)}
                      className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-red-50 text-xs text-red-700"
                      data-testid={`pu-delete-${u.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Deactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Surface>

      {editing && (
        <UnitEditor
          initial={editing}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function UnitEditor({ initial, products, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({
    name: initial.name || "",
    address: initial.address || "",
    email: initial.email || "",
    incharge_name: initial.incharge_name || "",
    mobile: initial.mobile || "",
    machines: initial.machines || [],
    product_ids: initial.product_ids || [],
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setMachine = (i, k, v) => {
    setForm((f) => {
      const arr = [...f.machines];
      arr[i] = { ...arr[i], [k]: v };
      return { ...f, machines: arr };
    });
  };

  const addMachine = () => set("machines", [...form.machines, { ...EMPTY_MACHINE }]);
  const removeMachine = (i) => set("machines", form.machines.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/production/units", form);
        toast.success("Production unit created");
      } else {
        await api.put(`/production/units/${initial.id}`, form);
        toast.success("Production unit updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "New Production Unit" : `Edit — ${initial.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pu-name" /></Field>
            <Field label="Incharge Name"><Input value={form.incharge_name} onChange={(e) => set("incharge_name", e.target.value)} data-testid="pu-incharge" /></Field>
            <Field label="Mobile"><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="pu-mobile" /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="pu-email" /></Field>
            <Field label="Address" className="col-span-2">
              <textarea className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 min-h-[70px]" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="pu-address" />
            </Field>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">Machines</p>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addMachine} data-testid="pu-add-machine">
                <Plus className="h-3 w-3" /> Add Machine
              </Button>
            </div>
            <div className="rounded-md border border-gray-100 divide-y divide-gray-100">
              {form.machines.length === 0 && (
                <p className="text-xs text-gray-400 py-3 text-center">No machines added</p>
              )}
              {form.machines.map((m, i) => (
                <div key={i} className="p-2 grid grid-cols-12 gap-2 items-center" data-testid={`pu-machine-${i}`}>
                  <Input placeholder="Name *" className="col-span-3 h-9 text-sm" value={m.name} onChange={(e) => setMachine(i, "name", e.target.value)} />
                  <Input placeholder="Model" className="col-span-3 h-9 text-sm" value={m.model || ""} onChange={(e) => setMachine(i, "model", e.target.value)} />
                  <Input placeholder="Capacity" className="col-span-2 h-9 text-sm" value={m.capacity || ""} onChange={(e) => setMachine(i, "capacity", e.target.value)} />
                  <Input placeholder="Notes" className="col-span-3 h-9 text-sm" value={m.notes || ""} onChange={(e) => setMachine(i, "notes", e.target.value)} />
                  <button onClick={() => removeMachine(i)} className="col-span-1 h-9 rounded hover:bg-red-50 text-red-600 flex items-center justify-center" aria-label="Remove machine">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-1.5 flex items-center gap-1.5">
              <Package className="h-3 w-3" /> Products Manufactured Here
            </p>
            <MultiSelect
              value={form.product_ids}
              onChange={(v) => set("product_ids", v)}
              options={products.map((p) => ({ value: p.id, label: p.name, hint: p.code }))}
              placeholder="Select products…"
              testId="pu-products"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D]" onClick={save} disabled={saving} data-testid="pu-save">
            {saving ? "Saving…" : (isNew ? "Create Unit" : "Save Changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
